import cn from "classnames";
import { useMemo, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { Logo, LogoSize } from "@components/new/Logo";
import LanguageSwitcherContainer from "@containers/LanguageSwitcherContainer/LanguageSwitcherContainer";
import InfoIcon from '@assets/icons/outline/info.svg?react';
import UniIcon from '@assets/icons/solid/uni-logo.svg?react';
import { getCurrentLanguage } from '@utils/lang';
import { getUniClubLevels } from '@api/apiClient/club';
import {
  useProblematicCustomsCounter,
  useProblematicFirstMileCounter,
} from '@api/apiClient/problematic';
import { useAwaitingCounter as useReturnsAwaitingCounter } from '@api/apiClient/returns';
import {
  useAwaitingCounter as useUnclaimedAwaitingCounter,
  useReturnsCounter,
} from '@api/apiClient';
import { useComplaintMessagesCounter } from '@api/apiClient/complaints';
import { useSellersCounter } from '@contexts/SellersCounterContext';

import { useProfile } from '@contexts/ProfileContext';
import { MenuItem } from "./components/MenuItem";
import getMenu from "./menu";
import styles from "./Sidebar.module.scss";

export const Sidebar = ({ isCollapsed }: {
  isCollapsed: boolean;
}) => {
  const { t } = useTranslation();
  const currentLanguage = getCurrentLanguage();
  const { account } = useProfile();
  const { sellersNotFound } = useSellersCounter();
  const { data: awaitingProblematicCustomsTotal } = useProblematicCustomsCounter(!sellersNotFound);
  const { data: awaitingProblematicFirstMileTotal } = useProblematicFirstMileCounter(!sellersNotFound);
  const { data: awaitingReturnsTotal } = useReturnsAwaitingCounter(!sellersNotFound);
  const { data: awaitingUnclaimedTotal } = useUnclaimedAwaitingCounter(!sellersNotFound);
  const { data: complaintsTotal, refetch: complaintsRefetch } = useComplaintMessagesCounter(!sellersNotFound);
  const { data: unclaimedReturnsCounter } = useReturnsCounter(!sellersNotFound);

  const { data: cashbackData } = useQuery({
    queryKey: ["getUniClubLevels"],
    queryFn: getUniClubLevels,
    retry: 1,
    select: (data) => (data?.currentCashback),
    enabled: !sellersNotFound && (account?.region === 'TR'),
  });

  useEffect(() => {
    const intId = setInterval(() => {
      complaintsRefetch();
    }, 20000);

    return () => {
      clearInterval(intId);
    }
  }, [])

  const items = useMemo(() => getMenu({
    t,
    account,
    complaintsTotal,
    awaitingReturnsTotal,
    awaitingUnclaimedTotal,
    awaitingProblematicCustomsTotal,
    awaitingProblematicFirstMileTotal,
    unclaimedReturnsCounter,
    cashbackData,
  }), [
    t,
    account,
    complaintsTotal,
    awaitingReturnsTotal,
    awaitingUnclaimedTotal,
    awaitingProblematicCustomsTotal,
    awaitingProblematicFirstMileTotal,
    unclaimedReturnsCounter,
    cashbackData,
    currentLanguage,
  ])

  return (
    <div className={cn(styles.sidebar, isCollapsed && styles.collapsed)}>
      <div className={styles.sidebarHead}>
        <Logo size={isCollapsed ? LogoSize.S : LogoSize.L} />
      </div>

      <div className={styles.navbar}>
        {items.map(v => (
          <MenuItem
            key={v.id}
            icon={v.icon}
            title={v.title}
            path={v.path}
            counter={v.count}
            disabled={v.disabled}
            subpages={v.subpages}
            collapsed={isCollapsed}
          />
        ))}
      </div>

      <div className={styles.footer}>
        {false ? (
          <>
            <MenuItem
              icon={InfoIcon}
              title={t("sidebar.help")}
              path=""
              collapsed={isCollapsed}
            />
            <MenuItem
              icon={UniIcon}
              title={t("sidebar.about")}
              path=""
              collapsed={isCollapsed}
            />
          </>
        ) : null}
        <LanguageSwitcherContainer collapsed={isCollapsed} />
      </div>

    </div>
  );
};
